/**
 * CASTLE ATLAS — Precision Storytelling Skill Config
 * ──────────────────────────────────────────────────────────────
 * Transparent card with Quill mascot.
 * Drawer slides from right on hover.
 * Layout: button at top, description, stats, badge, then title+tagline at bottom.
 * Percentage badge at bottom-left (monospace) reveals character name on click.
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Name-reveal timing — mirrors CloudTile's Easter egg on the module tiles
// (StorytellingLanding.jsx) exactly, so both read as the same trick.
const NAME_LETTER_STAGGER_MS = 45;
const NAME_LETTER_FADE_MS = 380;
const NAME_HOLD_MS = 3000;
const NAME_FADE_OUT_MS = 500;

export const PrecisionStorytellingCard = ({
  skill,
  progress = 0,
  isDark = false,
  mode = 'skills',
  onClick,
  onAction,
}) => {
  const [hovered, setHovered] = useState(false);
  // 'hidden' | 'shown' | 'fading' — same state machine as CloudTile's
  // Easter egg on the module tiles, replacing the old scale/opacity pop.
  const [namePhase, setNamePhase] = useState('hidden');
  const nameTimers = useRef([]);
  const clearNameTimers = () => { nameTimers.current.forEach(clearTimeout); nameTimers.current = []; };
  useEffect(() => clearNameTimers, []);
  const totalExercises = skill?.exercises ?? 20;
  const pct = totalExercises > 0 ? Math.round((progress / totalExercises) * 100) : 0;

  const mascotSrc = isDark
    ? '/precision-storytelling/mascot-night.png'
    : '/precision-storytelling/mascot-day.png';

  const mascotName = 'Quill';

  const accent = isDark ? '#f472b6' : '#db2777';
  const mutedColor = isDark ? '#9fb0d0' : '#5a6b86';

  const glassBg = isDark
    ? 'rgba(11, 16, 38, 0.65)'
    : 'rgba(255, 255, 255, 0.70)';
  const glassBorder = isDark
    ? 'rgba(255, 255, 255, 0.12)'
    : 'rgba(0, 0, 0, 0.06)';

  const wrapperProps = onClick
    ? {
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

  const revealName = (e) => {
    e.stopPropagation();
    clearNameTimers();
    setNamePhase('shown');
    const revealTotal = mascotName.length * NAME_LETTER_STAGGER_MS + NAME_LETTER_FADE_MS;
    nameTimers.current.push(setTimeout(() => setNamePhase('fading'), revealTotal + NAME_HOLD_MS));
    nameTimers.current.push(setTimeout(() => setNamePhase('hidden'), revealTotal + NAME_HOLD_MS + NAME_FADE_OUT_MS));
  };
  const handleNameKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    revealName(e);
  };

  return (
    <div
      {...wrapperProps}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        aspectRatio: '4 / 5',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 16,
        cursor: onClick ? 'pointer' : 'default',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* ─── Name reveal overlay (on top of character) — letter-by-letter,
          matching CloudTile's Easter egg on the module tiles exactly: same
          storyLetterIn keyframe (declared globally in index.css, reused
          here rather than redeclared), same stagger/hold/fade timing. ─── */}
      {namePhase !== 'hidden' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 30,
            pointerEvents: 'none',
            opacity: namePhase === 'fading' ? 0 : 1,
            transition: `opacity ${NAME_FADE_OUT_MS}ms ease`,
          }}
        >
          {/* Chip styling is a byte-for-byte port of .story-name-reveal /
              .dark .story-name-reveal from index.css (StorytellingLanding
              uses those classes; this card is isDark-prop-driven rather
              than .dark-class-driven, so the values are inlined here
              instead of sharing the class). */}
          <span
            style={{
              display: 'flex',
              padding: '7px 14px',
              borderRadius: 12,
              background: isDark ? 'rgba(18, 20, 28, 0.55)' : 'rgba(255, 255, 255, 0.55)',
              backdropFilter: 'blur(10px) saturate(160%)',
              WebkitBackdropFilter: 'blur(10px) saturate(160%)',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.16)' : '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: isDark
                ? 'inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 4px 14px rgba(0, 0, 0, 0.4)'
                : 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 4px 14px rgba(0, 0, 0, 0.18)',
              fontFamily: 'var(--font-skill-story)',
              fontWeight: 800,
              fontSize: 17,
              letterSpacing: '0.02em',
              color: isDark ? '#ffd9ec' : '#a3155a',
            }}
          >
            {[...mascotName].map((ch, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  opacity: 0,
                  animation: `storyLetterIn ${NAME_LETTER_FADE_MS}ms ease forwards`,
                  animationDelay: `${i * NAME_LETTER_STAGGER_MS}ms`,
                }}
              >
                {ch}
              </span>
            ))}
          </span>
        </div>
      )}

      {/* ─── Percentage badge at bottom-left (monospace) ─── */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Reveal ${mascotName}'s name`}
        style={{
          position: 'absolute',
          // % instead of px, and matches the content layer's
          // padding-bottom (36.5%, which itself resolves to the same 21%-
          // of-card-height cloud-bottom landmark — see that comment for
          // the full derivation). For an absolutely-positioned element,
          // `bottom` as a % resolves against the CONTAINING BLOCK'S
          // HEIGHT directly (unlike padding, which always uses width) —
          // so this is just the plain target fraction, no conversion
          // needed. This keeps the badge and the title block's bottom
          // edge sharing one line at any card width, both landing at the
          // cloud's actual bottom edge instead of an arbitrary offset.
          bottom: '21%',
          left: 16,
          zIndex: 20,
          fontFamily: 'var(--font-mono)',
          background: glassBg,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${glassBorder}`,
          borderRadius: 20,
          padding: '4px 12px',
          fontSize: 10, // matches the tiles' mono number badge (badgeBase.fontSize in CloudTile)
          fontWeight: 800,
          color: accent,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
        onClick={revealName}
        onKeyDown={handleNameKeyDown}
        onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? 'rgba(11,16,38,0.8)' : 'rgba(255,255,255,0.85)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = glassBg)}
      >
        {pct}%
      </div>

      {/* ─── Drawer – slides from right ───
          Split into a masked background layer + an unmasked content layer.
          It used to be ONE element with both the glass background AND the
          text as children, all under a single maskImage — that mask's
          transparent zone (0-40%) reached into where description text
          starts on long/full-width lines, so those characters rendered
          over see-through background instead of opaque glass. Now the
          mask only ever touches the background layer; content is never
          masked, and the transparent zone itself is pulled in tight so any
          fade stays confined to the sliver under/behind Quill. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '72%',
          height: '100%',
          transform: hovered ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          zIndex: 10,
          pointerEvents: hovered ? 'auto' : 'none',
        }}
      >
        {/* Background/blur layer only — the feathered mask lives here. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: glassBg,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderLeft: `1px solid ${glassBorder}`,
            borderRadius: '0 16px 16px 0',
            maskImage: 'linear-gradient(to right, transparent 0%, transparent 10%, black 26%, black 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, transparent 10%, black 26%, black 100%)',
          }}
        />
        {/* Content layer — never masked, always fully opaque.
            Bottom padding is a % (not px) so the title block's bottom edge
            — which sits flush against it, pushed there by the flex:1
            spacer below the status badge — tracks the cloud's actual
            bottom edge at ANY card width, not just one reference size.
            Derived from opening mascot-day.png directly: the cloud's
            lowest point sits ~87% down the 1024×1024 square. That square
            is object-fit:contain'd (98% sized) into this 4:5 card, which
            letterboxes it to ~10.8%–89.2% of the card's height — so the
            cloud-bottom landmark is ~79% of the card's height from the
            top, ~21% from the bottom. Percentage padding-bottom resolves
            against THIS element's own width (a fixed CSS rule — vertical
            padding always uses the containing block's width, even
            in a column layout), which is the drawer's width (72% of the
            card) — so 21% of the CARD's height converts to
            (0.21 × 1.25 × cardWidth) / (0.72 × cardWidth) ≈ 36.5% here.
            Top padding is a smaller, plain-px nudge — the button/
            description/stats cluster above it is text-height-driven, not
            proportional to card size, so it can only be approximated, not
            derived the same way. */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            padding: '22px 22px 36.5% 32px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
        <div style={{ textAlign: 'right', width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ─── TOP: Button ─── Compact — width:'100%' made it a wide bar
              across the whole (stretched) drawer; both variants now size to
              their own content and the wrapper right-aligns them instead. */}
          <div style={{ marginTop: '-20px', display: 'flex', justifyContent: 'flex-end' }}>   {/* move up 20px above character head */}
            {onAction ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAction();
                }}
                style={{
                  width: 'fit-content',
                  padding: '6px 12px',
                  borderRadius: 10,
                  border: `1px solid ${accent}66`,
                  background: isDark
                    ? 'rgba(219, 39, 119, 0.20)'
                    : 'rgba(219, 39, 119, 0.12)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: isDark ? '#fff' : accent,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? 'rgba(219,39,119,0.35)' : 'rgba(219,39,119,0.22)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = isDark ? 'rgba(219,39,119,0.20)' : 'rgba(219,39,119,0.12)')}
              >
                {pct > 0 ? `Continue · ${pct}%` : 'Start Storytelling'}
                <ArrowRight size={14} />
              </button>
            ) : (
              <Link
                to={skill?.route ?? '/skills/precision-storytelling'}
                style={{ textDecoration: 'none' }}
              >
                <button
                  style={{
                    width: 'fit-content',
                    padding: '6px 12px',
                    borderRadius: 10,
                    border: `1px solid ${accent}66`,
                    background: isDark
                      ? 'rgba(219, 39, 119, 0.20)'
                      : 'rgba(219, 39, 119, 0.12)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    color: isDark ? '#fff' : accent,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? 'rgba(219,39,119,0.35)' : 'rgba(219,39,119,0.22)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = isDark ? 'rgba(219,39,119,0.20)' : 'rgba(219,39,119,0.12)')}
                >
                  {pct > 0 ? `Continue · ${pct}%` : 'Start Storytelling'}
                  <ArrowRight size={14} />
                </button>
              </Link>
            )}
          </div>

          {/* ─── Description ─── margins tightened (was '12px 0 10px') so
              this sits just above the head-top landmark instead of
              trailing below it — see the stats/badge note below. */}
          <p
            style={{
              fontSize: 9.5, // was 11.5
              color: mutedColor,
              lineHeight: 1.5,
              margin: '10px 0 6px',
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2, // was 3 — exactly two lines
              WebkitBoxOrient: 'vertical',
            }}
          >
            {skill?.description}
          </p>

          {/* ─── Stats ─── Same landmark math as the content layer's
              padding-bottom comment: head-top sits ~17% of the card's
              height from the top. This row (and the status badge right
              after it) is the last thing before the flex:1 spacer, so
              tightening its own + the description's margins is what pulls
              it up toward that line — unlike the bottom landmark this
              isn't a single clean padding value (text height above it
              isn't proportional to card size), so it's an approximation,
              not a derived match. marginBottom tightened 4→2. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 16,
              marginBottom: 2,
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, color: mutedColor }}>
              {progress} of {totalExercises} exercises
            </span>
          </div>

          {/* ─── Status badge ───
              `float` has no effect on a flex item (this sits directly in
              the flex-column content layer), so it was inert — the real
              cause of the "stretched" look is this div being a flex child
              with the default align-items:stretch, forcing it to the
              container's full width despite display:inline-flex on itself.
              alignSelf:'flex-end' opts it out of that stretch and keeps it
              right-aligned, matching the drawer's right-leaning content;
              padding/font-size were already compact (smaller than
              Copywriting's own NOT STARTED pill), so no change needed
              there. The old clear:'both' sibling is dead now that float is
              gone. */}
          <div
            style={{
              display: 'inline-flex',
              alignSelf: 'flex-end',
              padding: '2px 10px',
              borderRadius: 99,
              border: `1px solid ${accent}66`,
              marginBottom: 'auto',
            }}
          >
            <span
              style={{
                fontSize: 8,
                fontWeight: 800,
                color: accent,
                letterSpacing: '0.06em',
              }}
            >
              {pct === 0
                ? 'NOT STARTED'
                : pct === 100
                ? 'COMPLETE ✓'
                : 'IN PROGRESS'}
            </span>
          </div>

          {/* ─── Spacer to push bottom section down ─── */}
          <div style={{ flex: 1 }} />

          {/* ─── BOTTOM: Tagline & Title (moved down 15px) ───
              Tagline sits above the title as a small eyebrow line, title
              roughly double its font-size. Both right-aligned now (was
              center, matching the module tiles' title) so they share the
              same right edge as the button/description/stats/badge above
              them, per this round's "right-align everything" — the
              tile-matching goal from two rounds ago is intentionally
              dropped here since it conflicted with that. */}
          <div style={{ marginTop: '15px', borderTop: `1px solid ${glassBorder}`, paddingTop: 12 }}>
            <p
              style={{
                fontSize: 12,
                color: accent,
                fontWeight: 700,
                fontStyle: 'italic',
                margin: '0 0 2px',
                textAlign: 'right',
              }}
            >
              {skill?.tagline ?? 'Pitch. Influence. Win.'}
            </p>
            <h3
              style={{
                fontFamily: 'var(--font-skill-story)',
                fontSize: 22, // ~10% down from 24
                fontWeight: 800,
                /* Branches on isDark like every other colour in this file.
                   It used to read hsl(var(--foreground)), the ambient
                   Tailwind token, which is the one thing here that does
                   NOT follow the isDark prop. That made the card depend on
                   the page's theme rather than on what it was told, so on
                   a dark surface inside a light-themed document the title
                   rendered near-black on near-black. */
                color: isDark ? '#f2f5fb' : '#1c2536',
                margin: 0,
                lineHeight: 1.2,
                textAlign: 'right',
              }}
            >
              {skill?.name ?? 'Precision Storytelling'}
            </h3>
          </div>
        </div>
        </div>
      </div>

      {/* ─── Character – large, 20px left ───
          Rendered AFTER the drawer with an explicit z-index above it (10)
          so Quill stays fully visible whether the drawer is open or
          closed — before this pass the drawer's z-index:10 sat above the
          character's implicit z-index:auto, so opening the drawer covered
          most of the mascot. pointerEvents:'none' so this purely-decorative
          layer never blocks clicks on the drawer's button/badge beneath it;
          the percentage badge (z-index 20) and name-reveal chip (z-index
          30) both still sit above the character, unaffected. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 15,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: 'translateX(-20px)',
        }}
      >
        <img
          src={mascotSrc}
          alt="Quill, storytelling mascot"
          style={{
            width: '98%',
            height: '98%',
            objectFit: 'contain',
            // The PNGs (mascot-day.png / mascot-night.png) aren't actually
            // transparent — confirmed by opening them directly: both are a
            // painted vignette (a soft light/cream glow around Quill fading
            // to solid black at the corners), not real alpha. There's no
            // image-editing tool in this environment to re-export them with
            // genuine transparency, so the blend mode is a CSS-only
            // workaround rather than a true fix.
            //
            // It has to differ by theme, and getting this wrong made Quill
            // completely invisible on the dark landing page:
            //
            //   multiply — WHITE pixels vanish, BLACK pixels stay black.
            //     Right on a LIGHT surface, where the cream glow needs to
            //     disappear. Catastrophic on a dark one: multiplying any
            //     colour against #0d0d0d floors the whole image to black,
            //     so Quill renders as a black square on a black page.
            //
            //   normal — shows the image as painted. Right on a DARK
            //     surface, because the vignette already fades to near
            //     black at the corners and therefore self-blends into
            //     TT.bg without any compositing trick at all.
            mixBlendMode: isDark ? 'normal' : 'multiply',
            filter: isDark
              ? 'drop-shadow(0 4px 16px rgba(0,0,0,0.5))'
              : 'drop-shadow(0 4px 16px rgba(0,0,0,0.06))',
            animation: 'mascotFloat 6s ease-in-out infinite',
            animationPlayState: hovered ? 'paused' : 'running',
          }}
        />
      </div>

      {/* storyLetterIn (the letter fade-in used above) lives globally in
          index.css — reused as-is, not redeclared here. */}
      <style>{`
        @keyframes mascotFloat {
          0%   { transform: translate(0, 0) rotate(0deg); }
          33%  { transform: translate(-5px, -8px) rotate(1.5deg); }
          66%  { transform: translate(4px, -4px) rotate(-1deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

export const SHAPE = 'cloud';
export const SKILL_ID = 'precision-storytelling';

export default PrecisionStorytellingCard;